// Client-side agent catalog used by the role-driven Home view and the
// Jobotics Agent page (which reads ?agentId=<slug>).
//
// Keep this in sync with server/data/agentRegistry.js — the SERVER owns the
// system prompt prefix and the canonical suggested queries; this file owns
// the visual card metadata (emoji, blurb, category, sources) plus a local
// copy of the suggested queries for rendering chips without an extra round-trip.
//
// Adding a new agent:
//   1. Add a registry entry on the server (with prompt prefix + queries).
//   2. Append an AGENTS entry below (id matching the server slug).
//   3. Mirror its suggestedQueries into AGENT_SUGGESTED_QUERIES.
//   4. Add the id to the relevant roles in JOB_TITLE_RECOMMENDATIONS.

export const JOB_TITLES = [
  { slug: 'project_manager', label: 'Project Manager' },
  { slug: 'estimator', label: 'Estimator' },
  { slug: 'superintendent', label: 'Superintendent' },
  { slug: 'foreman', label: 'Foreman' },
  { slug: 'owner_exec', label: 'Owner / Executive' },
  { slug: 'bim_vdc', label: 'BIM / VDC' },
  { slug: 'coordinator', label: 'Project Coordinator' },
  { slug: 'field_engineer', label: 'Field Engineer' },
  { slug: 'procurement', label: 'Procurement' },
  { slug: 'other', label: 'Other' }
];

export const DISCIPLINES = [
  { slug: 'mep_combined', label: 'MEP (combined)' },
  { slug: 'hvac', label: 'HVAC / Mechanical' },
  { slug: 'plumbing', label: 'Plumbing' },
  { slug: 'electrical', label: 'Electrical' },
  { slug: 'controls', label: 'Controls / BMS' },
  { slug: 'sheet_metal', label: 'Sheet Metal' },
  { slug: 'fire_protection', label: 'Fire Protection' },
  { slug: 'other', label: 'Other' }
];

const SOURCE_LABEL = {
  procore: 'Procore',
  vista: 'Vista',
  sharepoint: 'SharePoint',
  pinecone: 'KB',
  twin: 'Project Heatmap',
  upload: 'Upload',
  slack: 'Slack'
};

// Category-order used by the Home view's "Browse by category" section.
export const CATEGORY_ORDER = [
  'Recommended',
  'Construction',
  'Productivity',
  'Data Analysis',
  'A&E',
  'Inspection',
  'Jobsite Management'
];

// All 50 agents. `route: null` would mean "Coming soon" — there are none here.
// Default agent route pattern: `/jobotics-agent?agentId=<id>` (server prompt
// prefix kicks in via server/data/agentRegistry.js). Every agent uses it —
// contract_review used to be routed to /contract-redline instead, and now runs
// in chat so it can sweep contracts too long for that page's single pass.
const r = (id) => `/jobotics-agent?agentId=${id}`;

export const AGENTS = [
  // ---------- Recommended (5) ----------
  { id: 'deep_search', name: 'Deep Search Agent', emoji: '\u{1F48E}', category: 'Recommended',
    route: r('deep_search'),
    blurb: 'Search deeply across specs, drawings, RFIs, and submittals to get accurate, cited answers grounded in project documents.',
    sources: ['procore', 'sharepoint', 'upload'] },
  { id: 'rfi_validator', name: 'RFI Validator Agent', emoji: '\u{1F477}', category: 'Recommended',
    route: r('rfi_validator'),
    blurb: 'Validate RFIs before submission by identifying trivial requests and flagging cost, schedule, or quality implications.',
    sources: ['procore', 'sharepoint', 'upload'] },
  { id: 'scope_checker', name: 'Scope Checker Agent', emoji: '\u{1F50E}', category: 'Recommended',
    route: r('scope_checker'),
    blurb: 'Eliminate scope gaps and overlaps by reconciling contracts, drawings, and project metadata before they become issues.',
    sources: ['procore', 'sharepoint', 'upload'] },
  { id: 'submittal_builder', name: 'Submittal-Builder Agent', emoji: '\u{1F4D1}', category: 'Recommended',
    route: r('submittal_builder'),
    blurb: 'Build complete, properly formatted submittal packages from cover page to final PDF in a guided workflow.',
    sources: ['procore', 'sharepoint', 'upload'] },
  { id: 'document_comparison', name: 'Document Comparison Agent', emoji: '\u{1F4DD}', category: 'Recommended',
    route: r('document_comparison'),
    blurb: 'Compare drawing sets to identify material changes, scope creep, and project risk before they hit the field.',
    sources: ['upload'] },

  // ---------- Construction (13) ----------
  { id: 'single_line_drawings', name: 'Single-Line Drawings Expert', emoji: '⚡', category: 'Construction',
    route: r('single_line_drawings'),
    blurb: 'Extract and structure critical electrical system data from single-line drawings into clean, validated tables.',
    sources: ['upload'] },
  { id: 'change_order', name: 'Change Order Agent', emoji: '\u{1F4B5}', category: 'Construction',
    route: r('change_order'),
    blurb: 'Build, review, and substantiate change-order proposals with labor + material + markup grounded in MCAA / NECA.',
    sources: ['vista', 'upload'] },
  { id: 'summary_spec_submittal', name: 'Spec Summary Agent', emoji: '\u{1F4DC}', category: 'Construction',
    route: r('summary_spec_submittal'),
    blurb: 'Concise spec-section summaries so reviewers don\'t have to read 30 pages — onerous clauses called out verbatim.',
    sources: ['upload'] },
  { id: 'prequalification', name: 'Pre-Qualification Agent', emoji: '✅', category: 'Construction',
    route: r('prequalification'),
    blurb: 'Answer prequalification questions faster by reviewing uploaded checklists and supporting docs to complete forms.',
    sources: ['upload'] },
  { id: 'deep_dive_spec_submittal', name: 'Spec / Submittal Deep Dive', emoji: '\u{1F50D}', category: 'Construction',
    route: r('deep_dive_spec_submittal'),
    blurb: 'Deeply review submittal packages against specs to surface risks, scope gaps, and next steps before approvals create liabilities.',
    sources: ['upload'] },
  { id: 'schedule', name: 'Scheduler Agent', emoji: '\u{1F5D3}\u{FE0F}', category: 'Construction',
    route: r('schedule'),
    blurb: 'Analyze construction schedules (P6 .xer, MS Project .mpp) — float, critical path, recovery options.',
    sources: ['upload', 'twin'] },
  // Scheduling Agent — standalone multi-role schedule workspace (HTML
  // served at /api/scheduling-agent). external:true so the card opens
  // in a new tab rather than going through the SPA router. Backend
  // username allowlist (admin by default) gates access.
  { id: 'scheduling_agent', name: 'Scheduling Agent', emoji: '\u{1F4C5}', category: 'Construction',
    route: '/api/scheduling-agent/',
    external: true,
    blurb: 'Multi-role construction scheduling workspace — portfolio rollup, WBS Gantt, 3-6 week lookahead, and daily crew cards. Grounded in Sylvan project data, with an Anthropic agent that proposes sequenced plans.',
    sources: ['twin'] },
  { id: 'sld_review', name: 'SLD Review Agent', emoji: '\u{26A1}', category: 'Construction',
    route: '/api/sld-review/',
    external: true,
    blurb: 'Electrical one-line (single-line) diagram review: extracts the power graph from vector or scanned sheets (dual perception \u2014 local symbol detector + AI vision, cross-checked per lineup), runs a deterministic NEC rules engine (240.4, 250.122, 110.9, 450.3, Art. 430\u2026), anchors findings as red-line clouds on the sheet, and exports an audit-ready review memo PDF. Advisory screening \u2014 not a PE sign-off.',
    sources: ['upload'] },
  { id: 'equipment_schedule_extractor', name: 'Equipment Schedule Extractor', emoji: '\u{1F4CB}', category: 'Construction',
    route: r('equipment_schedule_extractor'),
    blurb: 'Pull mechanical / electrical equipment schedules out of drawings into clean structured tables.',
    sources: ['upload'] },
  { id: 'mep_takeoff', name: 'MEP Takeoff Agent', emoji: '\u{1F4CF}', category: 'Construction',
    route: r('mep_takeoff'),
    blurb: 'Quantity takeoff from PDF drawings for pipe, duct, conduit, wire, fittings, valves, hangers, and equipment.',
    sources: ['upload'] },
  { id: 'punchlist', name: 'Punchlist Agent', emoji: '\u{1F4CD}', category: 'Construction',
    route: r('punchlist'),
    blurb: 'Track MEP punchlist items from creation to closeout, with photo-based item creation and trade-grouped reports.',
    sources: ['upload', 'procore'] },
  { id: 'material_matrix_mep', name: 'Material Matrix Agent', emoji: '\u{1F9F1}', category: 'Construction',
    route: r('material_matrix_mep'),
    blurb: 'Generate a structured bill of materials from uploaded PDF drawings — MEP-only, with page citations.',
    sources: ['upload'] },
  { id: 'labor_estimator', name: 'Labor Estimator', emoji: '\u{1F4CA}', category: 'Construction',
    route: r('labor_estimator'),
    blurb: 'Defensible labor-hour estimates and cost rolls using MCAA / Weblem and NECA tables.',
    sources: ['vista', 'upload'] },
  { id: 'large_file', name: 'Large File Agent', emoji: '\u{1F4DA}', category: 'Construction',
    route: r('large_file'),
    blurb: 'Drawing sets / submittals up to 5 GB.',
    sources: ['upload'] },
  { id: 'rsi_extractor', name: 'MEP Agent', emoji: '\u{1F9E0}', category: 'Construction',
    route: r('rsi_extractor'),
    blurb: 'A single agent that handles five common MEP jobs — pulling data out of takeoffs, estimating labor or cost, checking against a code or spec, reviewing submittals, and reviewing drawings. Takes PDFs, Office files, IFC (BIM), DWF/DWFx (Autodesk), CSVs, and more. Figures out the document, runs, grades its own work, and tries again if it falls short.',
    sources: ['upload'] },
  { id: 'field_markup', name: 'Field Drawing Markup Agent', emoji: '\u{270F}\u{FE0F}', category: 'Construction',
    route: r('field_markup'),
    blurb: 'Interpret red-line / as-built markups on uploaded drawings and translate them into actionable change logs.',
    sources: ['upload'] },
  { id: 'drawing_markup', name: 'Drawing Markup Agent', emoji: '\u{1F58D}\u{FE0F}', category: 'Construction',
    route: r('drawing_markup'),
    blurb: 'Draws red-line markups on uploaded MEP and architectural drawing PDFs using three plain-English primitives: revision clouds (scalloped outlines around a region), arrow callouts (leader line with a labeled tag), and dashed scope boxes (rectangles framing a work area). Returns a downloadable annotated PDF with a change-log legend on the last page — and opens in the Markup Studio for page-by-page CRUD + AI chat.',
    sources: ['upload'] },
  { id: 'closeout', name: 'Closeout Agent', emoji: '\u{1F4E6}', category: 'Construction',
    route: r('closeout'),
    blurb: 'Build complete project closeout packages: O&M manuals, warranties, as-builts, training records, attic stock.',
    sources: ['upload', 'sharepoint'] },

  // ---------- Productivity (7) ----------
  { id: 'mentor', name: 'Mentor Agent', emoji: '\u{1F393}', category: 'Productivity',
    route: r('mentor'),
    blurb: 'Fast, clear guidance on MEP systems, processes, and standards — with the option to go deeper when needed.',
    sources: [] },
  { id: 'email_composer', name: 'Email Composer Agent', emoji: '\u{1F4E7}', category: 'Productivity',
    route: r('email_composer'),
    blurb: 'Draft project emails — RFI follow-ups, change-order claims, schedule notifications, owner updates.',
    sources: [] },
  { id: 'meeting_minutes', name: 'Meeting Minutes Agent', emoji: '\u{1F5D2}\u{FE0F}', category: 'Productivity',
    route: r('meeting_minutes'),
    blurb: 'Extract action items, decisions, and open issues from uploaded meeting minutes or recordings.',
    sources: ['upload'] },
  { id: 'document_translator', name: 'Document Translator Agent', emoji: '\u{1F310}', category: 'Productivity',
    route: r('document_translator'),
    blurb: 'Translate MEP project text between English and Spanish (or others) for field crews and bilingual coordination.',
    sources: ['upload'] },
  { id: 'permit_tracker', name: 'Permit Tracker Agent', emoji: '\u{1FAAA}', category: 'Productivity',
    route: r('permit_tracker'),
    blurb: 'Track plumbing, mechanical, electrical, fire protection, and elevator permit status across the project.',
    sources: ['upload'] },
  { id: 'vendor_quotes', name: 'Vendor Quote Comparison', emoji: '\u{1F4B0}', category: 'Productivity',
    route: r('vendor_quotes'),
    blurb: 'Normalize and compare equipment quotes from multiple vendors — like-for-like, spec compliance flagged.',
    sources: ['upload'] },
  { id: 'lessons_learned', name: 'Lessons Learned Agent', emoji: '\u{1F9E0}', category: 'Productivity',
    route: r('lessons_learned'),
    blurb: 'Capture and surface project lessons from closeouts, root-cause analyses, and post-mortems.',
    sources: ['upload', 'sharepoint'] },

  // ---------- Data Analysis (8) ----------
  { id: 'aec_research', name: 'AEC Research Agent', emoji: '\u{1F52C}', category: 'Data Analysis',
    route: r('aec_research'),
    blurb:
      'Research AEC macro trends — labor, wages, permits, materials, federal awards, emissions — and ship the answer as a PDF, Word doc, slide deck, dashboard, or executive email.',
    sources: ['pinecone', 'upload'] },
  { id: 'digital_twin', name: 'Project Heatmap Agent', emoji: '\u{1F3D7}\u{FE0F}', category: 'Data Analysis',
    // Internal /digital-twin-demo page (the existing rich charts UI). Gated
    // to a specific email allowlist (src/config/digitalTwinDemoAccess.js —
    // robert.dong@, jake@, skye@, admin@). The `requiresDigitalTwinAccess`
    // flag is read by Home/agentCatalog to filter the card out for everyone
    // else.
    // Note: user-facing name is "Project Heatmap" because "Digital Twin" has
    // a specific meaning in AEC tied to BIM. Internal id / route / flag
    // names kept as `digital_twin` to avoid breaking links + the server-side
    // allowlist code path.
    route: '/digital-twin-demo',
    requiresDigitalTwinAccess: true,
    blurb: 'Open the Project Heatmap — KPIs, health scores, burn, CPI/SPI, safety, RFIs, change orders.',
    sources: ['twin'] },
  { id: 'fast_ai_search', name: 'Fast AI Search', emoji: '\u{1F680}', category: 'Data Analysis',
    route: r('fast_ai_search'),
    blurb: 'Quick structured answers from connected spreadsheets, documents, databases, and the project heatmap.',
    sources: ['vista', 'twin', 'upload'] },
  { id: 'change_analyzer', name: 'Change Analyzer Agent', emoji: '\u{1F4C8}', category: 'Data Analysis',
    route: r('change_analyzer'),
    blurb: 'Analyze RFIs, change orders, and field changes to understand patterns, root causes, and cumulative impact.',
    sources: ['vista', 'procore'] },
  { id: 'bidding_intelligence', name: 'Bidding Intelligence', emoji: '\u{1F3AF}', category: 'Data Analysis',
    route: r('bidding_intelligence'),
    blurb: 'Surface win-rate patterns, competitor pricing signals, and recommended bid strategies from historical jobs.',
    sources: ['vista', 'upload'] },
  { id: 'job_profitability', name: 'Job Profitability Agent', emoji: '\u{1F4B8}', category: 'Data Analysis',
    route: r('job_profitability'),
    blurb: 'Analyze cost-to-complete, WIP, billing, and margin variance against the project heatmap.',
    sources: ['vista', 'twin'] },
  { id: 'labor_productivity', name: 'Labor Productivity Agent', emoji: '\u{1F4AA}', category: 'Data Analysis',
    route: r('labor_productivity'),
    blurb: 'Compare actual vs. estimated labor hours across crews, trades, and project phases.',
    sources: ['vista'] },
  { id: 'earned_value', name: 'Earned Value (CPI / SPI)', emoji: '\u{1F4D0}', category: 'Data Analysis',
    route: r('earned_value'),
    blurb: 'Compute and explain CPI and SPI on the project heatmap; project EAC and forecast at-completion variance.',
    sources: ['vista', 'twin'] },

  // ---------- Architecture & Engineering (6) ----------
  { id: 'rfi_checker', name: 'RFI Checker Agent', emoji: '\u{1F4E5}', category: 'A&E',
    route: r('rfi_checker'),
    blurb: 'Check draft RFIs against existing project docs to resolve internally before sending to the design team.',
    sources: ['upload'] },
  { id: 'sop', name: 'SOP Agent', emoji: '\u{1F4D6}', category: 'A&E',
    route: r('sop'),
    blurb: 'Review SOPs to surface gaps, compliance risks, and clear improvement recommendations.',
    sources: ['upload'] },
  { id: 'code_compliance', name: 'Code Compliance Agent', emoji: '\u{1F6E1}\u{FE0F}', category: 'A&E',
    route: r('code_compliance'),
    blurb: 'Check MEP designs and submittals against IMC, IPC, NEC, NFPA, and ASHRAE — code section cited verbatim.',
    sources: ['upload'] },
  { id: 'coordination_drawings', name: 'Coordination Drawings Agent', emoji: '\u{1F9E9}', category: 'A&E',
    route: r('coordination_drawings'),
    blurb: 'Surface clash candidates and trade-coordination issues from uploaded MEP / structural / arch drawings.',
    sources: ['upload'] },
  { id: 'conflict_finder', name: 'Conflict Finder', emoji: '\u{26A0}\u{FE0F}', category: 'A&E',
    route: r('conflict_finder'),
    blurb: 'Compare drawings, specs, submittals, and codes to flag inconsistencies ("docs show X, specs show Y") before they hit the field.',
    sources: ['upload'] },
  { id: 'duct_sizing', name: 'Duct Sizing Review Agent', emoji: '\u{1F4A8}', category: 'A&E',
    route: r('duct_sizing'),
    blurb: 'Check ductwork sizing against ASHRAE / SMACNA and the design intent — friction loss and fan power.',
    sources: ['upload'] },

  // ---------- Inspection (5) ----------
  { id: 'audit', name: 'Audit Agent', emoji: '\u{1F50F}', category: 'Inspection',
    route: r('audit'),
    blurb: 'Verify project documents against audit checklists and flag compliance gaps before audits become emergencies.',
    sources: ['upload', 'sharepoint'] },
  { id: 'pressure_test', name: 'Pressure Test Agent', emoji: '\u{1F4A7}', category: 'Inspection',
    route: r('pressure_test'),
    blurb: 'Manage pressure-test logs and certifications for hydronic, refrigerant, gas, and medical-gas systems.',
    sources: ['upload'] },
  { id: 'commissioning', name: 'Commissioning Agent', emoji: '\u{1F50C}', category: 'Inspection',
    route: r('commissioning'),
    blurb: 'Build Cx checklists, track functional test progress, and surface deficiencies per spec 23 08 00.',
    sources: ['upload'] },
  { id: 'insulation_inspection', name: 'Insulation Inspection Agent', emoji: '\u{1F9F6}', category: 'Inspection',
    route: r('insulation_inspection'),
    blurb: 'Verify pipe and duct insulation R-values, vapor barriers, and continuity against spec — photo-aware.',
    sources: ['upload'] },
  { id: 'ahj_inspection', name: 'AHJ Inspection Prep Agent', emoji: '\u{1F46E}', category: 'Inspection',
    route: r('ahj_inspection'),
    blurb: 'Prepare for AHJ inspections — pre-inspection checklist tailored to AHJ + inspection type.',
    sources: ['upload'] },

  // ---------- Jobsite Management (8) ----------
  { id: 'site_safety', name: 'Site Safety Agent', emoji: '\u{1F9BA}', category: 'Jobsite Management',
    route: r('site_safety'),
    blurb: 'Identify safety hazards from site photos, videos, and drawings with clear, field-ready findings.',
    sources: ['upload'] },
  { id: 'daily_report', name: 'Daily Report Agent', emoji: '\u{1F4F0}', category: 'Jobsite Management',
    route: r('daily_report'),
    blurb: 'Capture daily work activity quickly and generate a complete, structured daily report without missing details.',
    sources: ['upload'] },
  { id: 'toolbox_talk', name: 'Toolbox Talk Agent', emoji: '\u{1F529}', category: 'Jobsite Management',
    route: r('toolbox_talk'),
    blurb: 'Generate weekly toolbox talks tailored to MEP field crews — code-driven and discussion-ready.',
    sources: [] },
  { id: 'material_delivery', name: 'Material Delivery Agent', emoji: '\u{1F69A}', category: 'Jobsite Management',
    route: r('material_delivery'),
    blurb: 'Track and schedule material deliveries to the site — at-risk vs. rough-in date and hoisting coordination.',
    sources: ['procore', 'vista'] },
  { id: 'equipment_startup', name: 'Equipment Startup Agent', emoji: '\u{25B6}\u{FE0F}', category: 'Jobsite Management',
    route: r('equipment_startup'),
    blurb: 'Manage startup checklists for boilers, AHUs, chillers, pumps, panels — per manufacturer O&M.',
    sources: ['upload'] },
  { id: 'jha_jsa', name: 'JHA / JSA Agent', emoji: '\u{1FAA7}', category: 'Jobsite Management',
    route: r('jha_jsa'),
    blurb: 'Build Job Hazard Analyses for MEP tasks — step-by-step, OSHA-cited, foreman-signable.',
    sources: ['upload'] },
  { id: 'punchlist_photo', name: 'Punchlist Photo Agent', emoji: '\u{1F4F8}', category: 'Jobsite Management',
    route: r('punchlist_photo'),
    blurb: 'Extract punchlist items from jobsite photos — grouped by location, immediate hazards flagged.',
    sources: ['upload'] },
  { id: 'weather_impact', name: 'Weather Impact Agent', emoji: '\u{1F326}\u{FE0F}', category: 'Jobsite Management',
    route: r('weather_impact'),
    blurb: 'Quantify weather impact on the schedule and substantiate weather-day claims with NOAA-cited narrative.',
    sources: ['upload'] },

  // ---------- Contract Review — chat, and reads contracts of any length ----------
  // Runs in the agent chat now rather than routing to /contract-redline: the
  // sweep reads every page and reconciles across the document, which the
  // single-shot reviewer on that page cannot (it cuts at ~170 pages). The
  // redline page remains the place to get annotations written onto the PDF.
  { id: 'contract_review', name: 'Contract Review Agent', emoji: '\u{2696}\u{FE0F}', category: 'Recommended',
    route: r('contract_review'),
    blurb: 'Review a contract of any length for risk — payment, indemnity, change orders, liens, warranty — reading every page and flagging clauses that contradict each other across sections.',
    sources: ['upload'] },
];

// Suggested-query chips per agent (4 each). Mirrors the server registry.
// When the user opens /jobotics-agent?agentId=<id>, the page renders these
// instead of the default AEC chip row.
export const AGENT_SUGGESTED_QUERIES = {
  deep_search: [
    'What is the required insulation R-value for chilled water piping above ceiling?',
    'Where in the specs is the commissioning agent\'s role defined?',
    'Find all references to "warranty" across my uploaded contract documents.',
    'When is the substantial-completion milestone for the mechanical scope?',
  ],
  rfi_validator: [
    'Is this RFI trivial? The answer might already be on M-301.',
    'What cost and schedule implications does RFI #142 have on the AHU procurement?',
    'Draft a sharper version of this RFI so engineering can answer in one round-trip.',
    'Which spec sections govern the question in this draft RFI?',
  ],
  scope_checker: [
    'Compare my MEP scope of work against the drawings and flag gaps.',
    'Are there overlaps between mechanical and plumbing on the medical-gas scope?',
    'Does the contract exclude excavation for site utilities? The drawings imply we own it.',
    'What scope ambiguities exist between electrical and controls on the BMS?',
  ],
  submittal_builder: [
    'Build a complete submittal package for VRF condensing units per spec 23 81 26.',
    'What does the submittal for variable-frequency drives need to include?',
    'Compare my submittal package against spec 23 09 13 — what is missing?',
    'Draft the cover sheet and transmittal letter for the AHU submittal.',
  ],
  document_comparison: [
    'Compare these two issuances of the mechanical drawings and list every material change.',
    'What changed between spec rev 2 and rev 3 in section 23 31 13?',
    'Did the latest electrical drawing set add any new feeder paths?',
    'Compare the submitted shop drawings against the originally approved set.',
  ],
  single_line_drawings: [
    'Extract the panel schedule from this single-line drawing into a table.',
    'List every feeder shown on E-001 with size, type, and protection.',
    'What does the one-line say about the main service entrance configuration?',
    'Are there visible coordination gaps between the main breaker and downstream protective devices?',
  ],
  change_order: [
    'Build a change order for adding 200 LF of medical gas piping discovered during demo.',
    'What is the cost impact of replacing the specified pump with the submitted alternate?',
    'Review this change order narrative for completeness before I send it.',
    'Compile a log of every change order issued on this project, by trigger.',
  ],
  summary_spec_submittal: [
    'Summarize spec section 23 21 13 in under 200 words.',
    'What are the unusual or onerous clauses in spec 26 05 19?',
    'Give me a one-page brief of all mechanical spec sections.',
    'Compare summary of plumbing spec 22 11 16 against 22 13 16 for redundancy.',
  ],
  prequalification: [
    'Fill out this prequalification using my uploaded company docs — show what is missing.',
    'What is my 3-year EMR? Pull from uploaded safety records.',
    'List every analogous project on file for healthcare retrofit work.',
    'Generate a project list table with role, contract value, completion date, and reference.',
  ],
  deep_dive_spec_submittal: [
    'Review this pump submittal against spec 23 21 23 clause by clause.',
    'Is the submitted AHU compliant with the schedule on M-501?',
    'Find every "partial compliance" issue in my chiller submittal.',
    'Generate the approval letter draft based on the compliance matrix.',
  ],
  schedule: [
    'Which activities lost float this week and why?',
    'Give me a 3-week look-ahead with critical-path callouts.',
    'Compare baseline vs actual % complete by trade — where am I behind?',
    'What recovery options if the AHU delivery slips 4 weeks?',
  ],
  equipment_schedule_extractor: [
    'Extract the AHU schedule from M-501 into a table.',
    'Pull every panelboard from E-101 with its load summary.',
    'List all VAV boxes on the second floor with CFM and reheat capacity.',
    'Build a combined equipment list across all mechanical sheets.',
  ],
  mep_takeoff: [
    'Take off all chilled-water piping from M-401 and group by pipe size.',
    'Generate a duct takeoff with linear feet and weight from the supplied PDF.',
    'Pull every panel and feeder from E-201 — quantities and lengths.',
    'Build a bill of materials from the uploaded drawings.',
  ],
  punchlist: [
    'Create a punchlist from the attached walkthrough notes and photos.',
    'Sort my open punchlist items by trade and location for tomorrow\'s push.',
    'What does the attached photo show — is this a punch item?',
    'Build the final punchlist closeout report for the owner.',
  ],
  material_matrix_mep: [
    'Generate a BOM from these mechanical drawings into Excel.',
    'Build the electrical material matrix from E-201 and E-202.',
    'Combine the pipe + valve + fitting BOMs into one rolled-up sheet.',
    'Compare today\'s BOM against last week\'s for added scope.',
  ],
  labor_estimator: [
    'Estimate labor for 1200 LF of 4-inch black steel pipe with hangers every 8 ft.',
    'Roll up labor for this attached takeoff and export an Excel.',
    'Compare crew-mix assumptions for a 50-fixture electrical rough-in.',
    'What productivity factor for retrofit work in a tight existing ceiling?',
  ],
  field_markup: [
    'What changes are red-lined on this as-built sheet?',
    'Does the relocation marked on M-301 affect electrical routing on E-301?',
    'Build an as-built change log from the attached field-marked PDFs.',
    'Identify any markups that should have been RFIs but weren\'t.',
  ],
  closeout: [
    'What does our closeout package need per spec 01 78 00?',
    'List every warranty we owe the owner — and what\'s in our uploaded files vs. missing.',
    'Build the closeout transmittal cover letter.',
    'Compile the O&M manual index from the equipment schedules.',
  ],
  mentor: [
    'Explain hydronic loop balancing to a new foreman in 5 sentences.',
    'What is the difference between manual and automatic balancing valves?',
    'When do I need a flow switch on a chilled-water branch?',
    'Why does code require dielectric unions between dissimilar metals?',
  ],
  email_composer: [
    'Draft an email to the engineer asking for a response on RFI #142 by Friday.',
    'Write a change-order claim email to the GC for the discovered medical-gas scope.',
    'Compose a 3-week look-ahead update for the owner — keep it under 200 words.',
    'Send the punchlist closeout to the project team — use the on-file address.',
  ],
  meeting_minutes: [
    'Extract action items from this week\'s OAC meeting minutes.',
    'What decisions were made about the BMS scope across the last 4 meetings?',
    'Build a follow-up email to the GC capturing this meeting\'s asks.',
    'Compile a running log of open issues from all uploaded minutes.',
  ],
  document_translator: [
    'Translate this safety bulletin into Spanish for the rough-in crew.',
    'Side-by-side translation of this RFI text — English / Spanish.',
    'Translate spec 23 31 13 paragraph 2.1 into Spanish for the foreman.',
    'What does this Spanish daily report say in English?',
  ],
  permit_tracker: [
    'What\'s the status of every MEP permit on this project?',
    'Which permits are at risk of holding up the schedule?',
    'Draft the AHJ submittal cover letter for the mechanical permit revision.',
    'What does this AHJ comment letter require us to change in our drawings?',
  ],
  vendor_quotes: [
    'Compare these three AHU quotes side-by-side, normalized.',
    'Which quoted pump is non-compliant with the spec performance schedule?',
    'Build a vendor short-list recommendation with reasoning.',
    'Pull warranty terms out of each quote into a single table.',
  ],
  lessons_learned: [
    'Extract lessons learned from this closeout report into a wiki entry.',
    'What lessons from past hospital projects apply to our upcoming healthcare bid?',
    'Why did the schedule slip on Project 24-018? Build the post-mortem narrative.',
    'List every lesson tagged "underground utilities" across past projects.',
  ],
  aec_research: [
    'Show construction employment trends over the last 5 years and build an interactive dashboard with charts.',
    'What are prevailing wages for electricians in California — export to a formatted Excel workbook.',
    'Summarize federal construction awards over $10M in 2024 into a 5-slide executive deck.',
    'Track PPI for steel, concrete, and lumber the last 24 months; put takeaways in a one-page PDF handout.',
  ],
  digital_twin: [
    'What is the cost-to-date and percent complete on project 25-111?',
    'Compare CPI and SPI across all five demo jobs.',
    'Which project has the worst safety record this quarter?',
    'Show me the monthly burn trend for 24-042 with projected months called out.',
  ],
  fast_ai_search: [
    'What is total cost-to-date on project 25-111?',
    'How many open RFIs have we filed against the GC this month?',
    'Which projects are below 90% labor productivity this quarter?',
    'What was the average bid markup on healthcare jobs last year?',
  ],
  change_analyzer: [
    'What\'s the breakdown of change orders by trigger type on this project?',
    'Which RFIs were precursors to change orders? Show the pipeline.',
    'Cumulative cost impact of all design-error change orders this year.',
    'What process change would have prevented the top 3 change-order categories?',
  ],
  bidding_intelligence: [
    'What is my win rate on healthcare projects vs industrial this year?',
    'For a 120k sq ft healthcare retrofit, what bid range have we historically landed on?',
    'Which past projects are most analogous to this one and what did we win/lose on?',
    'Build an executive deck of bid pipeline and win patterns by sector.',
  ],
  job_profitability: [
    'Which projects have margin slipping more than 3% from the original bid?',
    'What is driving the variance on project 24-042?',
    'Build a margin-watch list for the operations meeting.',
    'Project final margin on project 25-203 based on current cost-to-date.',
  ],
  labor_productivity: [
    'Which crews are running below 0.85 productivity this month?',
    'Compare actual vs. estimated labor on the underground rough-in.',
    'Why is the controls crew showing 0.7 productivity? Dig into the data.',
    'Build a foreman scorecard showing productivity trends.',
  ],
  earned_value: [
    'What\'s the current CPI and SPI on project 25-111?',
    'Which WBS elements are dragging down the SPI?',
    'Project EAC for this project at the current run rate.',
    'Compare CPI trend over the last 6 weeks — is the slope getting worse?',
  ],
  rfi_checker: [
    'Is the answer to this draft RFI already in spec 23 21 13?',
    'Has the engineer already answered this in a prior RFI? Check the log.',
    'Sharpen this RFI so engineering can answer in one pass.',
    'Find prior RFIs related to medical-gas piping that might be analogous.',
  ],
  sop: [
    'Review our lockout / tagout SOP against OSHA 1910.147.',
    'What gaps exist in our confined-space entry SOP?',
    'Compare our welding SOP to NFPA 51B.',
    'Build the prioritized improvement list across all uploaded SOPs.',
  ],
  code_compliance: [
    'Is this plenum-return design compliant with IMC 2021 § 601.2?',
    'Check the panelboard locations against NEC 110.26 working-clearance requirements.',
    'Does this medical-gas zone design meet NFPA 99 chapter 5?',
    'Review the ductwork sizing for ASHRAE 90.1 fan-power compliance.',
  ],
  coordination_drawings: [
    'Find clash candidates between the ductwork on M-301 and the structural on S-301.',
    'Are there any hard clashes between conduit routing and the chilled-water branch?',
    'List coordination issues on level 3 above the ceiling.',
    'Propose resolutions for the three highest-severity clashes you found.',
  ],
  conflict_finder: [
    'Compare these mechanical drawings against spec 23 31 13 — flag any conflicts.',
    'Does the submitted pump match what the schedule on M-501 requires?',
    'Is the proposed plenum return setup compliant with IMC 2021 § 601.2?',
    'Find inconsistencies between the architectural and MEP drawings on level 2.',
  ],
  duct_sizing: [
    'Review the supply-duct sizing on M-301 — anything obviously undersized?',
    'Calculate friction loss for the run from AHU-1 to VAV-3 on M-501.',
    'Is the return-air shaft sized adequately for 18,000 CFM?',
    'Find every duct run that violates ASHRAE 90.1 fan-power.',
  ],
  audit: [
    'Audit my closeout package against the owner\'s checklist — what is missing?',
    'Find every required QA/QC document for the mechanical scope.',
    'Run a pre-audit on the safety records for the last 6 months.',
    'Build the action list to close every gap found before audit day.',
  ],
  pressure_test: [
    'Generate the pressure-test certification for the chilled-water riser.',
    'What test pressure and duration does spec 23 21 13 require for hydronic systems?',
    'Compile a closeout-grade pressure test log from all uploaded reports.',
    'Which pressure tests are still outstanding for this project?',
  ],
  commissioning: [
    'Build the Cx checklist for AHU-1 per spec 23 08 00.',
    'What integrated tests are required between the chiller plant and the BMS?',
    'Which Cx deficiencies are still open on this project?',
    'Generate the Cx report executive summary for the owner.',
  ],
  insulation_inspection: [
    'Check insulation R-values against spec 23 07 16 for every pipe size.',
    'From these photos, are there visible insulation gaps?',
    'Is a vapor barrier required on chilled-water piping above ceiling?',
    'Build the insulation inspection report for the owner walkthrough.',
  ],
  ahj_inspection: [
    'What does the AHJ typically focus on for a mechanical rough inspection?',
    'Build the inspector packet for tomorrow\'s plumbing top-out.',
    'Are there known AHJ hot-buttons we should pre-check this week?',
    'Draft a courtesy follow-up email to the inspector after today\'s walk.',
  ],
  site_safety: [
    'What hazards are visible in this site photo?',
    'Generate the JHA for installing the rooftop AHU.',
    'Review these photos and identify PPE gaps.',
    'Build the weekly safety bulletin for the rough-in crew.',
  ],
  daily_report: [
    'Build today\'s daily report from these notes and photos.',
    'What\'s the trend in mechanical crew size over the past 2 weeks?',
    'Compile a weekly roll-up from the last 5 daily reports.',
    'Generate next-day plan based on today\'s open work.',
  ],
  toolbox_talk: [
    'Generate this week\'s toolbox talk on confined-space entry.',
    'Build a toolbox talk for working around live electrical panels.',
    'Topic: silica dust during demo. Make it field-relevant for a 12-person crew.',
    'List the last 12 weeks of toolbox talks and propose next month\'s lineup.',
  ],
  material_delivery: [
    'What deliveries are scheduled this week?',
    'Is the AHU delivery on track for the rough-in window?',
    'Build a 30-day material-delivery look-ahead.',
    'Coordinate the hoisting plan for the rooftop chiller delivery.',
  ],
  equipment_startup: [
    'Build the startup checklist for the chiller plant per the manufacturer\'s O&M.',
    'Which pre-startup items are still open on AHU-1?',
    'What does the manufacturer require before the first boiler firing?',
    'Generate the startup-completion report for the owner.',
  ],
  jha_jsa: [
    'Build a JHA for setting the rooftop AHU.',
    'Generate the JHA for hot work in the boiler room.',
    'What controls should be in place for working over the data hall?',
    'Update the JHA based on the lessons-learned from yesterday\'s near miss.',
  ],
  punchlist_photo: [
    'Extract punchlist items from these walkthrough photos.',
    'Are any of these photos showing immediate safety hazards?',
    'Group today\'s photos into a punchlist sorted by floor and room.',
    'Compare today\'s photos against yesterday\'s — what got closed and what was added?',
  ],
  weather_impact: [
    'Build a weather-day claim for the rain events the last 2 weeks.',
    'How many days of exterior work have we lost to weather this month?',
    'What weather conditions trigger our contract\'s weather-day clause?',
    'Generate the weather-impact narrative for the schedule update.',
  ],
};

/**
 * Per-agent QUICK-mode chips. These replace AGENT_SUGGESTED_QUERIES when the
 * user toggles "Quick" on an agent page. Atomic, fast lookups — no uploads,
 * no planning step required. Each agent has 4.
 */
export const AGENT_QUICK_QUERIES = {
  deep_search: [
    'Required R-value for chilled-water pipe insulation, 4" size?',
    'List every "warranty" reference across uploaded docs.',
    'Substantial completion date for mechanical scope?',
    'Cite the spec section for commissioning requirements.',
  ],
  rfi_validator: [
    'Is this 1-line RFI trivial? Answer yes/no with reason.',
    'Top 3 cost/schedule implications of this draft RFI.',
    'Which spec section governs this question?',
    'Rewrite this RFI in 1 sentence.',
  ],
  scope_checker: [
    'List exclusions in my contract scope.',
    'Top 3 likely scope gaps vs. typical MEP drawings.',
    'Where is excavation responsibility usually ambiguous?',
    'Quick check: BMS scope split between EE and controls.',
  ],
  submittal_builder: [
    'List required submittal components for VRF condensing units.',
    'Standard submittal items per CSI Division 23.',
    'Cover sheet template for AHU submittal.',
    'Typical warranty submittal items.',
  ],
  document_comparison: [
    'Quick diff: list changed sheet numbers between revisions.',
    'Which spec sections were revised most recently?',
    'Top 5 callouts that typically change between issuances.',
    'What sheets usually drive coordination revisions?',
  ],
  single_line_drawings: [
    'Common one-line symbols cheat sheet.',
    'Typical service entrance config for 480/277V 3φ.',
    'Standard feeder sizing rules of thumb.',
    'Common arc-flash labeling requirements (NEC 110.16).',
  ],
  change_order: [
    'Standard markup % for T&M change orders.',
    'Typical labor burden rate for MEP work.',
    'Sample change-order narrative outline.',
    'Common triggers for unforeseen-conditions claims.',
  ],
  summary_spec_submittal: [
    'One-line summary of spec 23 21 13.',
    'Onerous clauses to watch for in MEP specs.',
    'Common warranty period requirements.',
    'Typical product-data submittal items.',
  ],
  prequalification: [
    'Standard prequalification fields list.',
    'What 3-year EMR is generally acceptable to GCs?',
    'Common references requested on prequal forms.',
    'Typical insurance limits for MEP contractors.',
  ],
  deep_dive_spec_submittal: [
    'Quick check: pump submittal vs. typical spec items.',
    'Top compliance categories for AHU submittals.',
    'Typical approval letter language.',
    'Common partial-compliance gotchas.',
  ],
  schedule: [
    'Define float, total float, and free float.',
    'Quick rule of thumb for critical-path identification.',
    'Typical recovery actions for 4-week material slip.',
    'Standard 3-week look-ahead structure.',
  ],
  equipment_schedule_extractor: [
    'Standard columns in an AHU schedule.',
    'Typical electrical info on an equipment schedule.',
    'Common VAV schedule structure.',
    'Panelboard schedule essentials.',
  ],
  mep_takeoff: [
    'Hangers per LF for 4" black steel pipe?',
    'Standard duct gauge per static class.',
    'Typical conduit fill rules for #12 THHN.',
    'Common takeoff units per system type.',
  ],
  punchlist: [
    'Standard punchlist severity scale.',
    'Common MEP punchlist categories.',
    'Typical closeout punch threshold for owner walks.',
    'Trade handoff order for punchlist resolution.',
  ],
  material_matrix_mep: [
    'Typical BOM columns for mechanical scope.',
    'Common electrical BOM line items.',
    'Standard material-matrix headers for procurement.',
    'BOM rollup formula by system type.',
  ],
  labor_estimator: [
    'MCAA hours for 100 LF of 2" copper Type L pipe.',
    'NECA hours for 100 LF of 3/4" EMT conduit.',
    'Productivity factor: tight ceiling retrofit?',
    'Labor for 1 ea 4" gate valve, flanged.',
  ],
  field_markup: [
    'Common red-line marks meaning cheat sheet.',
    'Typical as-built change-log structure.',
    'When does a markup require an RFI vs. just as-built?',
    'Standard markup color conventions.',
  ],
  closeout: [
    'Standard closeout checklist per spec 01 78 00.',
    'Typical owner training documentation requirements.',
    'Common attic stock requirements for MEP.',
    'O&M manual outline.',
  ],
  mentor: [
    'Define VAV vs. CAV in 2 sentences.',
    'Quick rule for hydronic system pressurization.',
    'When do I need a dielectric union?',
    'CFM per square foot for typical office space.',
  ],
  email_composer: [
    'Subject line for RFI follow-up email.',
    'Polite chase-up template for unanswered RFI.',
    'Owner update opener — 1 paragraph.',
    'Change-order claim subject line.',
  ],
  meeting_minutes: [
    'Standard OAC meeting agenda outline.',
    'Action-item template per attendee.',
    'Decision-log format.',
    'Open-issues table structure.',
  ],
  document_translator: [
    'English → Spanish: "Lockout/tagout required before energizing."',
    'English → Spanish: "Hot work permit on file."',
    'English → Spanish: "Watch for overhead crane lift."',
    'English → Spanish safety bulletin opener.',
  ],
  permit_tracker: [
    'Typical permit-application lead time per AHJ.',
    'Required documents for mechanical permit revision.',
    'Standard AHJ comment-letter response structure.',
    'Common permit fee categories.',
  ],
  vendor_quotes: [
    'Standard normalization columns for AHU quote comparison.',
    'Common quoted-vs-spec deltas to watch for.',
    'Quick freight / startup inclusion check.',
    'Warranty length comparison template.',
  ],
  lessons_learned: [
    'Lesson-entry template (root cause + remedy).',
    'Common lessons for underground utilities work.',
    'Typical hospital-project lessons learned themes.',
    'Standard post-mortem outline.',
  ],
  fast_ai_search: [
    'Cost-to-date project 25-111?',
    'Open RFI count this month?',
    'Below-90% productivity projects?',
    'Average bid markup, healthcare last year?',
  ],
  change_analyzer: [
    'Change-order categories typical for MEP scope.',
    'Top design-error change-order patterns.',
    'Typical cumulative-impact threshold for claims.',
    'Standard RFI → CO conversion rate.',
  ],
  bidding_intelligence: [
    'My win rate by sector last 12 months.',
    'Typical bid markup by building type.',
    'Most common loss reasons by segment.',
    'Average bid-spread on healthcare jobs.',
  ],
  job_profitability: [
    'Projects with margin variance > 3% this quarter.',
    'Top 3 jobs by negative variance.',
    'Forecasted EAC across active jobs.',
    'WIP position by job.',
  ],
  labor_productivity: [
    'Crews below 0.85 productivity this month.',
    'Productivity trend, last 6 weeks.',
    'Top underperforming activity types.',
    'Foreman scorecard quick view.',
  ],
  earned_value: [
    'CPI / SPI for project 25-111.',
    'WBS elements dragging SPI.',
    'EAC projection at current run rate.',
    'CPI 6-week trend.',
  ],
  rfi_checker: [
    'Standard internal-pre-RFI checklist.',
    'Common spec sections that answer typical RFIs.',
    'Top RFI categories that engineers refuse.',
    'Sharpen this RFI in 1 sentence.',
  ],
  sop: [
    'Lockout/tagout SOP essentials per OSHA 1910.147.',
    'Confined-space entry SOP outline.',
    'Hot-work SOP essentials per NFPA 51B.',
    'Welding SOP key points.',
  ],
  code_compliance: [
    'IMC 2021 plenum-return key requirement.',
    'NEC 110.26 working clearance summary.',
    'NFPA 99 medical-gas zone key reqs.',
    'ASHRAE 90.1 fan-power compliance summary.',
  ],
  coordination_drawings: [
    'Typical clash categories above ceiling.',
    'Standard coordination meeting agenda.',
    'BIM clash-detection thresholds.',
    'Common trade-stacking conflict patterns.',
  ],
  conflict_finder: [
    'Top spec-vs-drawing conflict categories.',
    'Common submittal-vs-spec mismatch types.',
    'Drawing-vs-code conflict checklist.',
    'Standard resolution-action labels.',
  ],
  duct_sizing: [
    'ASHRAE equal-friction default (in.wg/100ft)?',
    'Typical supply-duct velocity limits.',
    'Return-air shaft sizing rule of thumb.',
    'Static-regain method essentials.',
  ],
  audit: [
    'Standard QA/QC document checklist.',
    'Typical safety-record audit categories.',
    'Common closeout audit gaps.',
    'Pre-audit prep timeline.',
  ],
  pressure_test: [
    'Standard test pressure for hydronic systems.',
    'Typical test duration per spec.',
    'Medical-gas pressure-test sequence summary.',
    'Refrigerant pressure-test essentials.',
  ],
  commissioning: [
    'Cx pre-functional checklist essentials for AHU.',
    'Integrated test sequence: chiller plant ↔ BMS.',
    'Cx report executive-summary outline.',
    'Typical functional-test acceptance criteria.',
  ],
  insulation_inspection: [
    'Required R-value: chilled-water pipe, 4".',
    'Vapor barrier rule of thumb.',
    'Common insulation gap categories.',
    'Standard insulation inspection report fields.',
  ],
  ahj_inspection: [
    'AHJ mechanical-rough inspection focus areas.',
    'Typical plumbing top-out inspector hot-buttons.',
    'Standard inspector packet contents.',
    'Inspector follow-up email opener.',
  ],
  site_safety: [
    'Common PPE gap categories from photos.',
    'OSHA fall-protection trigger heights.',
    'Standard hot-work permit fields.',
    'Top 5 jobsite hazard categories.',
  ],
  daily_report: [
    'Standard daily-report template fields.',
    'Typical mechanical-crew composition.',
    'Standard weekly roll-up structure.',
    'Daily report next-day plan format.',
  ],
  toolbox_talk: [
    'Confined-space toolbox talk outline.',
    'Live-electrical-panel toolbox talk essentials.',
    'Silica-dust toolbox talk key points.',
    'Standard toolbox-talk discussion questions.',
  ],
  material_delivery: [
    'Standard delivery-log fields.',
    'Typical AHU delivery lead time.',
    'Rigging-plan essentials for rooftop chiller.',
    'Material-staging area rule of thumb.',
  ],
  equipment_startup: [
    'AHU pre-startup checklist essentials.',
    'Boiler firing pre-checks.',
    'Pump startup sequence summary.',
    'Standard startup-completion report outline.',
  ],
  jha_jsa: [
    'JHA outline for rooftop AHU install.',
    'JHA essentials for hot work in boiler room.',
    'Working-over-data-hall control checklist.',
    'Standard JHA review cadence.',
  ],
  punchlist_photo: [
    'Common defects visible in walkthrough photos.',
    'Severity scale for photo-derived punch items.',
    'Punchlist photo-tag conventions.',
    'Standard photo-walkthrough cadence.',
  ],
  aec_research: [
    'Construction employment trend, last 5 years.',
    'Prevailing wages for plumbers in Texas.',
    'Top 10 federal infrastructure awards 2024.',
    'Steel PPI year-over-year change.',
  ],
  weather_impact: [
    'Weather conditions triggering contract clause.',
    'Standard weather-day claim format.',
    'Typical productivity loss factor per weather event.',
    'NOAA data citation format for claims.',
  ],
};

// Role -> recommended agent ids, ordered by relevance. Home view renders the
// first 6 as "Recommended for you". Everything else is browse-by-category.
export const JOB_TITLE_RECOMMENDATIONS = {
  project_manager: ['rfi_validator', 'schedule', 'change_order', 'job_profitability', 'scope_checker', 'submittal_builder'],
  estimator: ['labor_estimator', 'bidding_intelligence', 'mep_takeoff', 'material_matrix_mep', 'aec_research', 'vendor_quotes'],
  superintendent: ['daily_report', 'punchlist', 'schedule', 'site_safety', 'material_delivery', 'equipment_startup'],
  foreman: ['daily_report', 'toolbox_talk', 'jha_jsa', 'punchlist_photo', 'document_translator', 'site_safety'],
  owner_exec: ['job_profitability', 'bidding_intelligence', 'aec_research', 'earned_value', 'change_analyzer', 'contract_review'],
  bim_vdc: ['coordination_drawings', 'conflict_finder', 'single_line_drawings', 'equipment_schedule_extractor', 'document_comparison', 'field_markup'],
  coordinator: ['rfi_validator', 'submittal_builder', 'meeting_minutes', 'permit_tracker', 'deep_search', 'schedule'],
  field_engineer: ['deep_search', 'rfi_checker', 'commissioning', 'pressure_test', 'punchlist', 'insulation_inspection'],
  procurement: ['vendor_quotes', 'material_delivery', 'contract_review', 'submittal_builder', 'bidding_intelligence', 'permit_tracker'],
  other: ['deep_search', 'fast_ai_search', 'rfi_validator', 'document_comparison', 'site_safety', 'mentor'],
};

/**
 * Filter agents by per-agent ACL. Currently the only gated agent is
 * `digital_twin` (requires email in digitalTwinDemoAccess.js's allowlist).
 * `acl` is { canAccessDigitalTwin: boolean }; pass it through from the caller
 * (Home.js owns the AuthContext + can call canAccessDigitalTwinDemo).
 */
function isAgentVisible(agent, acl = {}) {
  if (agent.requiresDigitalTwinAccess && !acl.canAccessDigitalTwin) return false;
  return true;
}

export function recommendedFor(jobTitleSlug, limit = 6, acl = {}) {
  const ids = JOB_TITLE_RECOMMENDATIONS[jobTitleSlug] || JOB_TITLE_RECOMMENDATIONS.other;
  const byId = Object.fromEntries(AGENTS.map((a) => [a.id, a]));
  return ids
    .map((id) => byId[id])
    .filter((a) => a && isAgentVisible(a, acl))
    .slice(0, limit);
}

/** All agents grouped by category in CATEGORY_ORDER for the browse view. */
export function agentsByCategory(excludeIds = [], acl = {}) {
  const exclude = new Set(excludeIds);
  const byCat = {};
  CATEGORY_ORDER.forEach((c) => { byCat[c] = []; });
  AGENTS.forEach((a) => {
    if (exclude.has(a.id)) return;
    if (!isAgentVisible(a, acl)) return;
    if (!byCat[a.category]) byCat[a.category] = [];
    byCat[a.category].push(a);
  });
  return CATEGORY_ORDER
    .map((c) => ({ category: c, agents: byCat[c] || [] }))
    .filter((g) => g.agents.length > 0);
}

export function sourceLabel(slug) {
  return SOURCE_LABEL[slug] || slug;
}

export function jobTitleLabel(slug) {
  return (JOB_TITLES.find((j) => j.slug === slug) || {}).label || slug;
}
